import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import Lenis from "lenis";
import { Crown, Images, CalendarHeart, Mail, Sparkles, BookOpen } from "lucide-react";
import { PAGES, PAGE_LABELS } from "@/components/site/siteData";
import { Bunny } from "@/components/vip/VipPage";
import { ArtPage } from "@/components/site/ArtPage";
import { AboutPage } from "@/components/site/AboutPage";
import { GalleryPage } from "@/components/site/GalleryPage";
import { ContactPage } from "@/components/site/ContactPage";
import BookingPage from "@/components/booking/BookingPage";
import { HouseDialog } from "@/components/site/HouseDialog";

const MOBILE_NAV = [
  ["home", "Home", Crown], ["menu", "Menu", BookOpen], ["gallery", "Gallery", Images],
  ["booking", "Book", CalendarHeart], ["contact", "Contact", Mail], ["vip", "VIP", Sparkles],
];

export default function Site() {
  const { page: param } = useParams();
  const navigate = useNavigate();
  const page = PAGES.includes(param) ? param : "home";
  const [menuOpen, setMenuOpen] = useState(false);
  const go = (p) => navigate(p === "home" ? "/" : `/${p}`);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let raf = requestAnimationFrame(function loop(t) { lenis.raf(t); raf = requestAnimationFrame(loop); });
    window.__lenis = lenis;
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);

  useEffect(() => {
    window.__lenis?.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
  }, [page]);

  const props = { go };
  return (
    <div className="site" data-testid="site-root">
      <motion.main
        key={page}
        initial={{ opacity: 0.25, y: 14, filter: "blur(8px)" }}
        animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        data-testid={`page-${page}`}
      >
        {page === "about" ? <AboutPage {...props} />
          : page === "gallery" ? <GalleryPage {...props} />
          : page === "booking" ? <BookingPage {...props} />
          : page === "contact" ? <ContactPage {...props} />
          : <ArtPage page={page} {...props} />}
      </motion.main>

      <nav className="mobile-nav" aria-label="Website pages" data-testid="mobile-nav">
        {MOBILE_NAV.map(([p, label, Icon]) => (
          <button key={p} type="button" onClick={() => go(p)} aria-current={page === p ? "page" : undefined}
            data-testid={`mobile-nav-${p}`}>
            <Icon size={19} strokeWidth={1.6} />{label}
          </button>
        ))}
      </nav>

      <motion.button type="button" className="tools-btn" onClick={() => setMenuOpen(true)}
        whileHover={{ rotate: 90, scale: 1.08 }} whileTap={{ scale: 0.92 }}
        aria-label="Explore the House" data-testid="explore-menu-button">
        <Sparkles size={20} />
      </motion.button>

      <Link to="/vip" className="vip-chip" data-testid="vip-nav-chip">
        <Bunny className="vip-chip-bunny" /> VIP Lounge
      </Link>

      <HouseDialog open={menuOpen} onOpenChange={setMenuOpen} title="Explore the House ♛"
        description="Choose a page." testId="explore-menu-dialog">
        <div className="explore-grid">
          {PAGES.map((p) => (
            <button key={p} type="button" className="cta" data-testid={`explore-go-${p}`}
              onClick={() => { setMenuOpen(false); go(p); }}>{PAGE_LABELS[p]}</button>
          ))}
        </div>
        <Link to="/vip" className="cta gold" onClick={() => setMenuOpen(false)} data-testid="explore-go-vip">
          ♛ The Bunny Bank VIP Lounge
        </Link>
        <a href="/owner" className="owner-link" data-testid="explore-owner-link">Owner login</a>
      </HouseDialog>
    </div>
  );
}
